import * as React from 'react';
import './featuredInsightModal.scss';
import { Modal } from 'rcd-cpp-ui/ui/modal/Modal';
import { Buttons, Primary, Secondary } from 'rcd-cpp-ui/ui/button';
import { SearchInput } from '../../SearchInput/searchInput';
import InfiniteLoader from '../../InfiniteLoader/infiniteLoader';
import { Metadata } from '../../../types';
import { useSelector } from '../../../utils/useSelector';
import { useLoader } from '../../Loader/loader';
import { useAlert } from '../../Alert/alert';
import { useActions } from './featuredInsightActions';
import { Card } from '../RelationshipField/searchCard';
import { Icon } from 'rcd-cpp-ui/ui/icon/Icon';
import { Close } from 'rcd-cpp-ui/ui/icon/Remove';
import { ItemLink } from '../../ItemLink/itemLink';

type Props = {
  data?: Partial<Metadata>;
  onSave: (selectedItem: Metadata | Metadata[]) => Promise<any>;
  onClose: () => any;
};

type SearchResp = {
  pages: number;
  data: Metadata[];
};

export const FeaturedInsightModal = (p: Props) => {
  const { data, onClose } = p;
  const getAPI = useSelector(s => s.app?.elFn?.getAPI);

  const actions = useAlert({ ...useActions(), onSave: p.onSave });
  const { isLoading, Loader, Alert, onSave, searchItems } = useLoader(
    useAlert(actions)
  );

  const scrollContainer = React.useRef(null);
  const [isInfiniteScrolling, setIsInfiniteScrolling] = React.useState(false);
  const [initialFeaturedInsights, setInitialFeaturedInsights] = React.useState<
    Metadata[]
  >([]);
  const [selectedCards, setSelectedCards] = React.useState<Metadata[]>([]);
  const [searchResult, setSearchResult] = React.useState<SearchResp>(null);
  const [query, setQuery] = React.useState('');
  const [page, setPage] = React.useState(0);

  const isCardSelected = (card: Metadata) => {
    return selectedCards.some(
      selected => selected?.props?.refId === card?.props?.refId
    );
  };

  const toggleCard = (card: Metadata) => {
    if (isCardSelected(card)) {
      setSelectedCards(
        selectedCards.filter(c => c?.props?.refId !== card?.props?.refId)
      );
    } else {
      setSelectedCards([...selectedCards, card]);
    }
  };

  React.useEffect(() => {
    const initial = data?.props?.featuredInsights || [];
    setInitialFeaturedInsights(initial);
    if (initial.length > 0) {
      setSelectedCards([...initial]);
    }
  }, [data?.props?.featuredInsights]);

  const hasSelectionChanged = () => {
    if (initialFeaturedInsights.length !== selectedCards.length) return true;
    return !selectedCards.every(
      (card, index) =>
        card?.props?.refId === initialFeaturedInsights[index]?.props?.refId
    );
  };

  React.useEffect(() => {
    doSearch();
  }, []);

  const doSearch = React.useCallback(() => {
    if (page > 0) setPage(0);
    setSearchResult(null);
    searchItems(data, query, 0).then((r: any) => setSearchResult(r));
  }, [query, page, getAPI]);

  React.useEffect(() => {
    if (page > 0) {
      setIsInfiniteScrolling(true);
      actions
        .searchItems(data, query, page)
        .then((r: any) => setSearchResult(r))
        .finally(() => setIsInfiniteScrolling(false));
    }
  }, [page]);

  return (
    <div className="shared-modal slider-activator">
      <Modal
        id="featuredInsightModal"
        onClose={onClose}
        header={'Set Up Featured Insights'}
        footer={
          <Buttons>
            <Secondary label="Cancel" onClick={onClose} />
            <Primary
              label="Save"
              onClick={async () => {
                await onSave(selectedCards);
                onClose();
              }}
              disabled={isLoading || !hasSelectionChanged()}
            />
          </Buttons>
        }
      >
        <Alert />
        <div className="modal-panels">
          <div className="modal-panel">
            <div className="select-link">Link to Featured Insight</div>
            <span className="input-group">
              <input
                id={`relationship-search`}
                className={'form-control'}
                value={query}
                onChange={q => setQuery(q?.target?.value)}
                onKeyDown={async e => {
                  if (e.key === 'Enter') {
                    doSearch();
                  }
                }}
                placeholder="Search"
                onFocus={event => {
                  event.target.setAttribute('autocomplete', 'off');
                }}
              />
              <SearchInput onClick={doSearch} />
            </span>

            <div
              className="search-result-container scrollable"
              ref={scrollContainer}
            >
              <Loader />
              {searchResult?.data?.length == 0 && (
                <div>No Search Results Found</div>
              )}
              {searchResult?.data?.map(r => {
                const isSelected = isCardSelected(r);
                return (
                  <Card
                    key={r?.props?.refId}
                    cardData={r}
                    highlight={isSelected}
                    onClick={() => toggleCard(r)}
                  />
                );
              })}
              {!!searchResult?.pages && searchResult?.pages > page && (
                <InfiniteLoader
                  container={scrollContainer}
                  loadData={() => {
                    setPage(page + 1);
                  }}
                  loading={isInfiniteScrolling}
                />
              )}
            </div>
          </div>

          <div className="modal-panel right-panel">
            <div className="selected-insights-header">
              Selected Insights ({selectedCards.length})
            </div>
            <div className="selected-insights-list">
              {selectedCards.length > 0 ? (
                <ul className="featured-insight-group">
                  {selectedCards.map((insight: Metadata) => (
                    <li key={insight.props?.refId}>
                      <div>
                        <span>• </span>
                        <ItemLink
                          refId={insight.props?.refId}
                          title={insight.props?.title}
                        />
                        <Icon onClick={() => toggleCard(insight)}>
                          <Close />
                        </Icon>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="no-selection">No insights selected</div>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};
